import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface CourtAvailability {
  id: string;
  court_name: string;
  court_number: number;
  day_of_week: string;
  start_time: string;
  end_time: string;
  week_numbers: number[] | null;
}

interface CourtBooking {
  id: string;
  event_id: string;
  week_number: number;
  court_name: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  booked_by: string;
  registration_id: string;
  status: string;
}

export interface CourtSlot {
  courtName: string;
  courtNumber: number;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  isBooked: boolean;
  bookedByCurrentUser: boolean;
  bookingId?: string;
  availabilityId: string;
}

export function useCourtBooking(eventId: string, selectedWeek: number, matchDuration: number = 90, weekStartDate?: Date) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Get user's registration for this event
  const { data: userRegistration } = useQuery({
    queryKey: ['user-registration-booking', eventId, user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('registrations')
        .select('id')
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .or(`user_id.eq.${user.id},partner_user_id.eq.${user.id}`)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!eventId && !!user?.id,
  });

  // Fetch court availability for the selected week
  const { data: availability, isLoading: isLoadingAvailability } = useQuery({
    queryKey: ['court-availability', eventId, selectedWeek],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('league_court_availability')
        .select('*')
        .eq('event_id', eventId)
        .or(`week_numbers.cs.{${selectedWeek}},week_numbers.is.null`);
      if (error) throw error;
      return data as CourtAvailability[];
    },
    enabled: !!eventId && selectedWeek > 0,
  });

  // Fetch existing bookings for the selected week
  const { data: bookings, isLoading: isLoadingBookings } = useQuery({
    queryKey: ['court-bookings', eventId, selectedWeek],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('league_court_bookings')
        .select('*')
        .eq('event_id', eventId)
        .eq('week_number', selectedWeek);
      if (error) throw error;
      return data as CourtBooking[];
    },
    enabled: !!eventId && selectedWeek > 0,
  });

  // Generate time slots from availability
  const slots: CourtSlot[] = useMemo(() => {
    if (!availability) return [];
    
    const result: CourtSlot[] = [];
    
    for (const avail of availability) {
      const startMinutes = timeToMinutes(avail.start_time);
      const endMinutes = timeToMinutes(avail.end_time);
      
      // Calculate the expected date for this day's slot
      const expectedDate = weekStartDate 
        ? getDateForDay(weekStartDate, avail.day_of_week) 
        : undefined;
      
      let current = startMinutes;
      while (current + matchDuration <= endMinutes) {
        const slotStart = minutesToTime(current);
        const slotEnd = minutesToTime(current + matchDuration);
        
        // Check if this slot is booked - match by date too when available
        const booking = bookings?.find(
          b => b.court_name === avail.court_name && 
               b.start_time === slotStart && 
               b.end_time === slotEnd &&
               (!expectedDate || b.booking_date === expectedDate)
        );
        
        result.push({
          courtName: avail.court_name,
          courtNumber: avail.court_number,
          dayOfWeek: avail.day_of_week,
          startTime: slotStart,
          endTime: slotEnd,
          isBooked: !!booking,
          bookedByCurrentUser: booking?.booked_by === user?.id,
          bookingId: booking?.id,
          availabilityId: avail.id,
        });
        
        current += matchDuration;
      }
    }
    
    return result;
  }, [availability, bookings, matchDuration, user?.id, weekStartDate]);

  // Current user's booking for this week
  const userBooking = useMemo(() => {
    return bookings?.find(b => b.booked_by === user?.id) || null;
  }, [bookings, user?.id]);

  const hasBookingThisWeek = !!userBooking;

  // Book a court
  const bookMutation = useMutation({
    mutationFn: async (slot: CourtSlot) => {
      if (!user?.id || !userRegistration?.id) throw new Error('No registration found');
      
      // Calculate booking_date from the week's start date + day offset
      // For now use today as placeholder - the component will pass the actual date
      const { data, error } = await supabase
        .from('league_court_bookings')
        .insert({
          event_id: eventId,
          week_number: selectedWeek,
          court_name: slot.courtName,
          booking_date: weekStartDate 
            ? getDateForDay(weekStartDate, slot.dayOfWeek)
            : new Date().toISOString().split('T')[0],
          start_time: slot.startTime,
          end_time: slot.endTime,
          booked_by: user.id,
          registration_id: userRegistration.id,
          status: 'confirmed',
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-bookings', eventId, selectedWeek] });
      toast.success('¡Cancha reservada exitosamente!');
    },
    onError: (error: any) => {
      if (error.message?.includes('unique') || error.code === '23505') {
        toast.error('Este horario ya fue reservado por otro jugador');
      } else {
        toast.error('Error al reservar cancha');
      }
      console.error('Booking error:', error);
    },
  });

  // Cancel a booking
  const cancelMutation = useMutation({
    mutationFn: async (bookingId: string) => {
      const { error } = await supabase
        .from('league_court_bookings')
        .delete()
        .eq('id', bookingId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['court-bookings', eventId, selectedWeek] });
      toast.success('Reserva cancelada');
    },
    onError: () => {
      toast.error('Error al cancelar reserva');
    },
  });

  const bookCourt = (slot: CourtSlot) => {
    if (hasBookingThisWeek) {
      toast.error('Ya tienes una reserva para esta semana');
      return;
    }
    bookMutation.mutate(slot);
  };

  const cancelBooking = (bookingId: string) => {
    cancelMutation.mutate(bookingId);
  };

  return {
    slots,
    userBooking,
    hasBookingThisWeek,
    isLoading: isLoadingAvailability || isLoadingBookings,
    isBooking: bookMutation.isPending,
    isCancelling: cancelMutation.isPending,
    bookCourt,
    cancelBooking,
    hasRegistration: !!userRegistration,
  };
}

// Helpers
function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60).toString().padStart(2, '0');
  const m = (minutes % 60).toString().padStart(2, '0');
  return `${h}:${m}:00`;
}

function getDayOffset(dayOfWeek: string): number {
  const map: Record<string, number> = {
    lunes: 0, monday: 0,
    martes: 1, tuesday: 1,
    miércoles: 2, miercoles: 2, wednesday: 2,
    jueves: 3, thursday: 3,
    viernes: 4, friday: 4,
    sábado: 5, sabado: 5, saturday: 5,
    domingo: 6, sunday: 6,
  };
  return map[dayOfWeek.toLowerCase()] ?? 0;
}

function getDateForDay(weekStart: Date, dayOfWeek: string): string {
  const offset = getDayOffset(dayOfWeek);
  const date = new Date(weekStart);
  date.setDate(date.getDate() + offset);
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}
